import React, { useEffect, useRef, useState } from 'react'
import { injectIntl, useIntl } from 'react-intl'
import { useTheme, FormHelperText, TextField, Chip } from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import QuestionContainer from './QuestionContainer'
import Typography from '@material-ui/core/Typography'
import { Field, change, touch } from 'redux-form'
import { checkboxRequired } from '../../../../utilties/validators'
import FocusTrap from 'focus-trap-react'
import { useSyncRefHeight } from '../../../../utilties/utilities'
import { Autocomplete } from '@material-ui/lab'

const requiredValidator = checkboxRequired


const CheckboxEl = ({
	isOneLine,
	touched,
	error,
	input,
	onAnswerChanged,
	r,
	...custom
}) => {
	const itemsRef = useRef(null)

	
	useSyncRefHeight(
		[
			[`multi_items_${r.id}`, itemsRef],
		],
		[ r ],
	)
	
	
	return (
		<Grid ref={itemsRef} item sm={12} lg={!!isOneLine ? 12 : 6}>
			<FormControl
				data-testid="multiselect-question"
				error={Boolean(touched && error)} fullWidth
			>
				<FormControlLabel
					data-testid="multiselect-question-control"
					style={{
						margin: '0px'
					}}
					control={
						<Checkbox
							color="primary"
							data-testid="multiselect-question-checkbox"
							name={`${input.name}[${r.id}]`}
							onChange={(event) => {
								const newValue = [ ...input.value ]
								if (event.target.checked) {
									newValue.push(r.id)
								} else {
									newValue.splice(newValue.indexOf(r.id), 1)
								}

								onAnswerChanged(r, event.target.checked)

								return input.onChange(newValue)
							}}
							value={r.id}
							inputProps={{ 'aria-labelledby': r.label }}
							checked={input.value.indexOf(r.id) !== -1}
							{...custom}
						/>
					}
					label={
						<Typography
							data-testid="multiselect-question-label"
							variant='subtitle2'>
							{r.label}
						</Typography>
					}
				/>
			</FormControl>
		</Grid>
	)
}

const CheckboxElContainer = ({ input, options, initialState, label, classes, meta: { touched, error }, showError, onAnswerChanged, isOneLine, ...custom }) => {

	if (!input.value && options && options.initialState) {
		input.onChange(options.initialState);
    }
	return (
		<Grid data-testid="survey-writable-checkboxes" container style={{ width: '100%' }}
			className="container-questions-grid"
		>
			{options.items &&
				options.items.map((r, i) => {
					return (
						<CheckboxEl
							isOneLine={isOneLine}
							touched={touched}
							error={error}
							input={input}
							onAnswerChanged={onAnswerChanged}
							r={r}
							{...custom}
						/>
					)
				})}
			{touched &&
			error &&
			showError && (
				<span data-testid="multi-select-question-error" className="errorWrapper">{error}</span>
			)}
		</Grid>
	)
}

const FormDropdown = ({ input, options, initialState, label, classes, meta: { touched, error, invalid, form, name, dispatch }, showError, onAnswerChanged, isOneLine, disabled, ...custom }) => {
	const intl = useIntl()

	const [values, setValues] = useState([])

	useEffect(
		() => {
			if (!options.initialState?.length) {
				return
			}

			setValues([...options.items.filter(x => options.initialState.includes(x.id))])
		},
		[options]
	)


	const setValue = (event, newValues) => {
		if (disabled) return

		if (!!onAnswerChanged) {
			let checked = newValues.length > values.length
			let answer = null
	
			if (!!checked) {
				answer = newValues.filter(x => !values.filter(y => x.id == y.id).length)[0]
			}
			else {
				answer = values.filter(x => !newValues.filter(y => x.id == y.id).length)[0]
			}

			onAnswerChanged(answer, !!checked)

			dispatch(change(form, input.name, newValues.map(x => x.id)))
		}

		setValues(newValues)
	}
	
	return (
		<Grid data-testid="survey-writable-checkboxes" container style={{ width: '100%' }}
			className="container-questions-grid"
		>
			<Autocomplete
				data-testid="writable-multiple-autocomplete"
				openOnFocus
				multiple
				value={values}
				onChange={(event, newValue) => {
					dispatch(touch(form, name))
					setValue(
						event,
						[ ...newValue ]
					);
				}}
				options={options?.items}
				getOptionLabel={(option) => option.label}
				disableCloseOnSelect
				renderOption={(props, option) => {
					return (
						<span
							{...props}
						>
						<Checkbox
							color='Black'
							style={{
								marginRight: 8
							}}
							checked={option.selected}
						/>
							{props.label}
						</span>
					)}
				}
				renderTags={(tagValue, getTagProps) =>
					tagValue
						.sort((a, b) => a.sortOrder > b.sortOrder ? 1 : a.sortOrder < b.sortOrder ? -1 : 0)
						.map((option, index) => (
							<Chip
								label={option.label}
								size='medium'
								style={{
									padding: 0,
									fontSize: '14px'
								}}
								{...getTagProps({ index })}
								onDelete={!!disabled ? undefined : getTagProps({ index }).onDelete}
							/>
						))
				}
				fullWidth
				renderInput={(params) => (
					<TextField
						variant='outlined'
						placeholder={intl.formatMessage({id: 'app.survey.question.dropdownPlaceholder'})}
						{...params}
					/>
				)}
				{...custom}
				disabled={disabled}
			/>

			{touched &&
			error &&
			showError && (
				<FormHelperText
					className="errorWrapper"
					data-testid="writable-grouped-error-multiselect">
					{error}
				</FormHelperText>
			)}
		</Grid>
	)
}

const MultiSelectQuestion = ({
	question,
	position,
	positionDisplay,
	disabled,
	onQuestionValueChanged,
	isBilateral,
	isBilateralFullInfo,
	displayInstructionAsIcon,
	uniqueKey
}) => {
	const isLabelContainerPreserved = !!isBilateral && !!isBilateralFullInfo
	const name = `answers.multiple._${question.uniqueId}._${uniqueKey ?? ''}`

	const [ all, setAll ] = useState([])
	const [answers, setAnswers] = useState(null)
	const [ trapIsActive, setTrapIsActive ] = useState(false)

	useEffect(() => {
		if (document.getElementById(name) !== null) {
			document.getElementById(name).addEventListener('keydown', (e)=>{
				if (e.keyCode === 13) {
					e.preventDefault()
					setTrapIsActive(true)
				}
				if (e.keyCode === 27) {
					e.preventDefault()
					setTrapIsActive(false)
				}
			})
		}
	}, [])

	const handleAnswerChanged = (answer, value) => {
		onQuestionValueChanged(question, value, answer, undefined)
	}

	useEffect(
		() => {
			if (question && question.answers) {
				let i = []
				question.answers.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1)).map((r) => {
					i.push(r)
				})
				setAll(i)

                let items = i.filter(e => e.checked).map(e => e.id);

				if (answers === null && items && items.length)
                    setAnswers(items);
            }
		},
		[question, answers ]
	)
	return (
		<Grid>
			<QuestionContainer
				data-testid="multiselect-question-container"
				title={`${question.title}`}
				titleRequered={`${question.isOptional ? '' : ' *'}`}
				position={position}
				positionDisplay={positionDisplay}
				instructions={question.instructions}
				isBilateral={isBilateral}
				displayInstructionAsIcon={displayInstructionAsIcon}
				children={
					<Grid
						container
					>
						{
							!!isLabelContainerPreserved
							&& <Grid xs={6} />
						}
						<Grid
							item
							xs={isLabelContainerPreserved ? 6 : 12}
						>
							<Grid container direction='column' style={{ width: '100%' }} id={name}>
								<Grid item style={{ width: '100%' }}>
									<FocusTrap active={trapIsActive}
										focusTrapOptions={{
											returnFocusOnDeactivate: false
										}}>
											
											{all && (
												<Field
													disabled={disabled}
													component={!question.isDropdown ? CheckboxElContainer : FormDropdown}
													name={name}
													validate={question.isOptional ? null : requiredValidator}
													options={{ items: all, initialState: answers}}
													showError={true}
													isOneLine={isBilateral}
													onAnswerChanged={handleAnswerChanged}
												/>					
											)}
									</FocusTrap>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				}
			/>
		</Grid>
	)
}

export default injectIntl(MultiSelectQuestion)
